import { useMemo, useState } from 'react';
import { ArrowRight, Sparkles, Loader2, RefreshCw, ChevronDown } from 'lucide-react';
import {
  generatePlan,
  findFixtureExercise,
  localizedExerciseName,
  type ClientInput,
  type Plan,
  type RuleTraceEntry,
} from '@app/rule-engine';
import { playgroundCopy, type Locale } from '@/copy';
import { cn } from '@/lib/cn';

const APP_URL = import.meta.env.VITE_APP_URL ?? 'https://app.fitfive.app';

type FormState = {
  age: number;
  sex: 'male' | 'female' | 'other';
  experience_level: 'beginner' | 'intermediate' | 'advanced';
  primary_goal: 'fat_loss' | 'muscle_gain' | 'strength' | 'endurance' | 'general_fitness';
  days_per_week: number;
  session_duration_min: number;
  equipment_available: string[];
  injuries_text: string;
};

const DEFAULT_FORM: FormState = {
  age: 32,
  sex: 'male',
  experience_level: 'intermediate',
  primary_goal: 'muscle_gain',
  days_per_week: 4,
  session_duration_min: 60,
  equipment_available: ['barbell', 'dumbbells', 'cables', 'bodyweight'],
  injuries_text: '',
};

const EQUIPMENT_OPTIONS = ['barbell', 'dumbbells', 'cables', 'machines', 'bands', 'bodyweight'] as const;

interface Result {
  plan: Plan;
  trace: RuleTraceEntry[];
}

export function PlanPlayground({ locale }: { locale: Locale }) {
  const t = playgroundCopy[locale];
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [result, setResult] = useState<Result | null>(null);
  const [generating, setGenerating] = useState(false);

  const submit = () => {
    setGenerating(true);
    // Synthetic delay so users feel the work (10ms is too fast; 250 feels alive).
    setTimeout(() => {
      const input: ClientInput = {
        id: 'playground',
        age: form.age,
        sex: form.sex,
        weight_kg: 75,
        height_cm: 178,
        experience_level: form.experience_level,
        primary_goal: form.primary_goal,
        equipment_available: form.equipment_available as ClientInput['equipment_available'],
        schedule: {
          days_per_week: form.days_per_week,
          session_duration_min: form.session_duration_min,
        },
        injuries: parseInjuries(form.injuries_text),
      };
      const { plan, trace } = generatePlan({
        client: input,
        options: { weeks: 4, start_date: new Date().toISOString().slice(0, 10) },
      });
      setResult({ plan, trace });
      setGenerating(false);
    }, 250);
  };

  const reset = () => {
    setResult(null);
    setForm(DEFAULT_FORM);
  };

  const toggleEquipment = (item: string) => {
    setForm((prev) => ({
      ...prev,
      equipment_available: prev.equipment_available.includes(item)
        ? prev.equipment_available.filter((i) => i !== item)
        : [...prev.equipment_available, item],
    }));
  };

  return (
    <section id="playground" className="border-y border-border bg-card/40">
      <div className="mx-auto max-w-5xl px-4 py-20">
        <header className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
            <Sparkles className="h-3 w-3 text-primary" />
            {t.label}
          </div>
          <h2 className="mt-4 text-balance text-3xl font-bold tracking-tight md:text-4xl">
            {t.title}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">{t.subtitle}</p>
        </header>

        {result ? (
          <PlanResult result={result} t={t} locale={locale} onReset={reset} />
        ) : (
          <PlanForm
            form={form}
            setForm={setForm}
            t={t}
            generating={generating}
            onSubmit={submit}
            onToggleEquipment={toggleEquipment}
          />
        )}
      </div>
    </section>
  );
}

// =============================================================================
// Form
// =============================================================================

function PlanForm({
  form,
  setForm,
  t,
  generating,
  onSubmit,
  onToggleEquipment,
}: {
  form: FormState;
  setForm: (next: FormState | ((prev: FormState) => FormState)) => void;
  t: (typeof playgroundCopy)['pl'];
  generating: boolean;
  onSubmit: () => void;
  onToggleEquipment: (item: string) => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="mt-10 grid gap-6 rounded-xl border border-border bg-card p-6 md:p-8"
    >
      <div className="grid gap-4 md:grid-cols-3">
        <Field label={t.field_age}>
          <NumberInput
            value={form.age}
            min={16}
            max={80}
            onChange={(v) => setForm((p) => ({ ...p, age: v }))}
          />
        </Field>
        <Field label={t.field_sex}>
          <Segmented
            value={form.sex}
            options={[
              { value: 'male', label: t.sex_male },
              { value: 'female', label: t.sex_female },
              { value: 'other', label: t.sex_other },
            ]}
            onChange={(v) => setForm((p) => ({ ...p, sex: v as FormState['sex'] }))}
          />
        </Field>
        <Field label={t.field_experience}>
          <Segmented
            value={form.experience_level}
            options={[
              { value: 'beginner', label: t.exp_beginner },
              { value: 'intermediate', label: t.exp_intermediate },
              { value: 'advanced', label: t.exp_advanced },
            ]}
            onChange={(v) =>
              setForm((p) => ({ ...p, experience_level: v as FormState['experience_level'] }))
            }
          />
        </Field>
      </div>

      <Field label={t.field_goal}>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
          {(['fat_loss', 'muscle_gain', 'strength', 'endurance', 'general_fitness'] as const).map(
            (g) => (
              <button
                key={g}
                type="button"
                onClick={() => setForm((p) => ({ ...p, primary_goal: g }))}
                className={cn(
                  'rounded-md border px-3 py-2 text-sm transition-colors',
                  form.primary_goal === g
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-background hover:bg-muted',
                )}
              >
                {t[`goal_${g}` as const]}
              </button>
            ),
          )}
        </div>
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label={t.field_days}>
          <NumberInput
            value={form.days_per_week}
            min={2}
            max={6}
            onChange={(v) => setForm((p) => ({ ...p, days_per_week: v }))}
          />
        </Field>
        <Field label={t.field_duration}>
          <NumberInput
            value={form.session_duration_min}
            min={20}
            max={120}
            step={5}
            onChange={(v) => setForm((p) => ({ ...p, session_duration_min: v }))}
          />
        </Field>
      </div>

      <Field label={t.field_equipment}>
        <div className="flex flex-wrap gap-2">
          {EQUIPMENT_OPTIONS.map((eq) => {
            const active = form.equipment_available.includes(eq);
            return (
              <button
                key={eq}
                type="button"
                onClick={() => onToggleEquipment(eq)}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm transition-colors',
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-background hover:bg-muted',
                )}
              >
                {t[`eq_${eq}` as const]}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label={t.field_injuries} hint={t.field_injuries_hint}>
        <input
          type="text"
          value={form.injuries_text}
          onChange={(e) => setForm((p) => ({ ...p, injuries_text: e.target.value }))}
          placeholder={t.injuries_placeholder}
          className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </Field>

      <button
        type="submit"
        disabled={generating || form.equipment_available.length === 0}
        className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-primary px-6 font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
      >
        {generating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> {t.generating}
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" /> {t.generate}
          </>
        )}
      </button>
      <p className="text-center text-xs text-muted-foreground">{t.disclaimer}</p>
    </form>
  );
}

// =============================================================================
// Result
// =============================================================================

function PlanResult({
  result,
  t,
  locale,
  onReset,
}: {
  result: Result;
  t: (typeof playgroundCopy)['pl'];
  locale: Locale;
  onReset: () => void;
}) {
  const [expandedWeek, setExpandedWeek] = useState(1);
  const [showTrace, setShowTrace] = useState(false);

  const summary = useMemo(() => {
    const totalExercises = result.plan.weeks.reduce(
      (acc, w) => acc + w.days.reduce((a, d) => a + d.exercises.length, 0),
      0,
    );
    const exclusions = result.trace.filter((e) => e.decision.startsWith('excluded')).length;
    return { totalExercises, exclusions };
  }, [result]);

  return (
    <div className="mt-10 space-y-6">
      <div className="grid gap-4 rounded-xl border border-border bg-card p-6 md:grid-cols-4 md:p-8">
        <SummaryCell label={t.summary_weeks} value={result.plan.weeks.length.toString()} />
        <SummaryCell label={t.summary_split} value={result.plan.split.replace('_', ' / ')} />
        <SummaryCell label={t.summary_exercises} value={summary.totalExercises.toString()} />
        <SummaryCell
          label={t.summary_excluded}
          value={summary.exclusions.toString()}
          hint={summary.exclusions > 0 ? t.summary_excluded_hint : undefined}
        />
      </div>

      <div className="rounded-xl border border-border bg-card p-6 md:p-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold">{t.preview_title}</h3>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-muted"
          >
            <RefreshCw className="h-3 w-3" />
            {t.regenerate}
          </button>
        </div>

        <div className="space-y-3">
          {result.plan.weeks.map((week) => {
            const open = expandedWeek === week.week_number;
            return (
              <div key={week.week_number} className="rounded-md border border-border bg-background">
                <button
                  type="button"
                  onClick={() => setExpandedWeek(open ? -1 : week.week_number)}
                  className="flex w-full items-center justify-between px-4 py-3 text-left text-sm"
                >
                  <span className="font-medium">
                    {t.week_label.replace('{n}', String(week.week_number))}
                    {week.is_deload ? (
                      <span className="ml-2 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-primary">
                        {t.deload_badge}
                      </span>
                    ) : null}
                  </span>
                  <ChevronDown
                    className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
                  />
                </button>
                {open ? (
                  <div className="space-y-3 border-t border-border bg-muted/30 p-4">
                    {week.days.map((d) => (
                      <div
                        key={d.day_number}
                        className="rounded border border-border bg-background p-3"
                      >
                        <div className="mb-2 flex items-center justify-between">
                          <span className="font-medium text-sm">
                            {t.day_label.replace('{n}', String(d.day_number))} ·{' '}
                            <span className="text-muted-foreground">{d.focus}</span>
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {d.target_duration_min} min
                          </span>
                        </div>
                        <ul className="space-y-1 text-sm">
                          {d.exercises.map((e, idx) => (
                            <li
                              key={`${e.exercise_id}-${idx}`}
                              className="flex items-baseline justify-between gap-3"
                            >
                              <span>
                                {(() => {
                                  const fx = findFixtureExercise(e.exercise_id);
                                  return fx ? localizedExerciseName(fx, locale) : e.exercise_name;
                                })()}
                              </span>
                              <span className="font-mono text-xs text-muted-foreground">
                                {e.sets}×{e.reps} · RPE {e.rpe_target}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <button
          type="button"
          onClick={() => setShowTrace((v) => !v)}
          className="flex w-full items-center justify-between px-6 py-4 text-left"
        >
          <div>
            <h3 className="text-sm font-semibold">{t.trace_title}</h3>
            <p className="text-xs text-muted-foreground">{t.trace_subtitle}</p>
          </div>
          <ChevronDown
            className={cn('h-4 w-4 transition-transform', showTrace && 'rotate-180')}
          />
        </button>
        {showTrace ? (
          <div className="space-y-2 border-t border-border bg-muted/20 p-6">
            {result.trace.map((entry, idx) => (
              <div
                key={`${entry.rule}-${idx}`}
                className="rounded-md border border-border bg-background p-3"
              >
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono uppercase text-primary">{entry.rule}</span>
                  <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                    {entry.decision}
                  </span>
                </div>
                <p className="mt-1 text-sm">{entry.reason}</p>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="rounded-xl border border-primary bg-primary/5 p-6 text-center md:p-8">
        <h3 className="text-lg font-semibold">{t.cta_title}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{t.cta_body}</p>
        <a
          href={`${APP_URL}/signup`}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t.cta_button} <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

// =============================================================================
// Primitives
// =============================================================================

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-2">
      <span className="block text-sm font-medium">{label}</span>
      {hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}
      {children}
    </label>
  );
}

function NumberInput({
  value,
  onChange,
  min,
  max,
  step,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step ?? 1}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (Number.isFinite(n)) onChange(n);
      }}
      className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
    />
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex rounded-md border border-border bg-background p-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            'flex-1 rounded px-2 py-1.5 text-xs transition-colors',
            value === opt.value
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function SummaryCell({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function parseInjuries(text: string): ClientInput['injuries'] {
  if (!text.trim()) return [];
  return text
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((body_part) => ({ body_part, severity: 'unknown' as const }));
}
